import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { InlineAlert } from './States';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
}

const sizeClasses = {
  sm: 'max-w-[420px]',
  md: 'max-w-[520px]',
  lg: 'max-w-[680px]',
  xl: 'max-w-[840px]',
  // xxl is for inline document viewers (resume preview, artefact
  // viewer) where the iframe needs room to render an A4 page at a
  // legible size without horizontal scroll.
  xxl: 'max-w-[1100px]',
};

// Selector for everything Tab can land on inside the modal panel.  Used by
// the focus trap to find first/last and cycle.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = `modal-title-${title.replace(/\s+/g, '-').toLowerCase()}`;

  // Hold the latest `onClose` in a ref so the open-modal effect
  // below can call it without listing `onClose` as a dependency.
  // Parents pass `onClose={() => setOpen(false)}` inline, which gives
  // it a new identity on every parent render — putting it in the
  // dependency array caused the effect to tear down + re-run on
  // every keystroke in a child input, and the re-run's `enterFocus`
  // call yanked focus back to the Close button after each letter
  // typed. Stable ref breaks that loop.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Body scroll-lock — without this the page scrolls behind the backdrop
    // on every modal in the app. Compensate for the disappearing scrollbar
    // by padding the body so layout doesn't shift.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Move focus into the panel after the next paint so children have mounted.
    const enterFocus = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => el.offsetParent !== null);
      // Prefer the first interactive control (skip the close button which is
      // visually last so Tab from inside cycles back to it).
      const first = focusables[0] ?? panel;
      first.focus();
    };
    const raf = window.requestAnimationFrame(enterFocus);

    // Mark everything outside the modal inert so screen-reader virtual
    // cursors and Tab can't escape.  We tag and untag siblings of the
    // portal mount instead of the whole body so the modal subtree itself
    // stays interactive.
    const portalRoot = panelRef.current?.parentElement?.parentElement; // panel -> backdrop wrapper -> portal root
    const inertedSiblings: HTMLElement[] = [];
    if (portalRoot) {
      const root = document.getElementById('root');
      if (root && root !== portalRoot) {
        root.setAttribute('inert', '');
        root.setAttribute('aria-hidden', 'true');
        inertedSiblings.push(root);
      }
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      // Focus trap: cycle Tab/Shift-Tab inside the modal.
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      for (const el of inertedSiblings) {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
      }
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      // Return focus to whatever opened the modal (button, link, etc.)
      // unless that element is no longer in the DOM.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose
  // accessed through onCloseRef so the effect doesn't re-run when the
  // parent re-creates the arrow on every render. See ref above.
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-4 prism-enter"
      role="presentation"
    >
      {/* Backdrop — navy blur to match brand */}
      <div
        className="absolute inset-0 bg-[var(--color-navy)]/40 backdrop-blur-[2px]"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={[
          'relative z-10 flex max-h-[85vh] w-full flex-col bg-[var(--color-surface)] rounded-[6px]',
          'border border-[var(--color-rule)]',
          'shadow-[0_24px_60px_-20px_rgba(25,54,97,0.25)]',
          'focus:outline-none',
          sizeClasses[size],
        ].join(' ')}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[var(--color-rule)] px-6 py-4">
          <h2
            id={titleId}
            className="text-[18px] font-semibold leading-tight text-[var(--color-ink)]"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="-mr-1 -mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[4px] text-[22px] leading-none text-[var(--color-ink-soft)] transition-colors hover:bg-[var(--color-rule)] hover:text-[var(--color-ink)]"
          >
            &times;
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  warning?: string;
  confirmLabel?: string;
  confirmVariant?: 'primary' | 'danger' | 'success';
  isLoading?: boolean;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  warning,
  confirmLabel = 'Confirm',
  confirmVariant = 'primary',
  isLoading = false,
}: ConfirmModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      {warning && (
        <div className="mb-3">
          <InlineAlert variant="warn">
            <span className="font-semibold uppercase tracking-[0.08em]">Warning</span>
            {' — '}
            {warning}
          </InlineAlert>
        </div>
      )}
      <p className="whitespace-pre-line text-[14.5px] leading-relaxed text-[var(--color-ink-muted)]">
        {message}
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant={confirmVariant}
          onClick={onConfirm}
          isLoading={isLoading}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}


// ─────────────────────────────────────────────────────────────────────
// TypedConfirmModal — destructive-action guard.
//
// The user must echo a specific string (typically the target's email)
// before the Confirm button enables. This is UX safety, not security:
// it stops the muscle-memory "click yes to dismiss" reflex on
// irreversible actions like hard-delete.
// ─────────────────────────────────────────────────────────────────────

interface TypedConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (typed: string) => void;
  title: string;
  /** The phrase the user must type verbatim (case-insensitive) to
   * enable the Confirm button. Typically the target's email. */
  requiredPhrase: string;
  /** Free-form pre-amble that explains what's about to happen. */
  message: ReactNode;
  /** Field label above the typed-confirm input. */
  inputLabel?: string;
  confirmLabel?: string;
  isLoading?: boolean;
}

export function TypedConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  requiredPhrase,
  message,
  inputLabel = 'Type the email above to confirm',
  confirmLabel = 'Delete',
  isLoading = false,
}: TypedConfirmModalProps) {
  const [typed, setTyped] = useState('');
  // Reset on close so a future open starts clean.
  useEffect(() => {
    if (!isOpen) setTyped('');
  }, [isOpen]);

  const matches =
    typed.trim().toLowerCase() === requiredPhrase.trim().toLowerCase();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <div className="text-[14.5px] leading-relaxed text-[var(--color-ink-muted)]">
        {message}
      </div>
      <div className="mt-5 rounded-[3px] border border-[var(--color-rule)] bg-[var(--color-surface-muted)] px-3 py-3">
        <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-[var(--color-ink-soft)]">
          Type to confirm
        </p>
        <p className="mt-1 font-mono text-[14px] text-[var(--color-ink)]">
          {requiredPhrase}
        </p>
      </div>
      <div className="mt-3">
        <label className="font-mono text-[11.5px] uppercase tracking-[0.14em] text-[var(--color-ink-soft)]">
          {inputLabel}
        </label>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={isLoading}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          className="mt-1.5 block w-full rounded-[3px] border border-[var(--color-rule)] bg-white px-3 py-2 font-mono text-[13.5px] text-[var(--color-ink)] focus:border-[var(--color-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
        />
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={() => onConfirm(typed)}
          disabled={!matches || isLoading}
          isLoading={isLoading}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
